import React, { useState, useEffect } from "react";
import {
  Grid,
  CircularProgress,
  Button,
  Typography,
  Modal,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import "../assets/css/header.css";
import "../assets/css/BoxButton.css";
import ModalConferma from "../components/Generics/ModalConferma";
import EValouForm from "../components/EValouForm";
import { service } from "../model";
import PopupError from "../components/Generics/PopupError";
import PopupSuccess from "../components/Generics/PopupSuccess";
import { useSelector } from "react-redux";
import { sectionsFirst, sectionsSecond } from "../utils/sectionValuo";
import ModalSuccess from "../components/Generics/ModalSuccess";
import ModalError from "../components/Generics/ModalError";

// PAGINA VALUTAZIONE
const EvaluoFormPage = ({ user, loadAppData }) => {
  const [localLoading, setLocalLoading] = useState(true);

  const isProfessionista = user.role === "collaborator";
  const isPartner = user.role === "partner";

  const { id } = useParams();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenComplete, setModalOpenComplete] = useState(false);
  const reduxObject = useSelector((state) => state.user?.redux_object);
  const token = reduxObject.accessToken;

  const [eValuo, setEValuo] = useState({});
  const [errors, setErrors] = useState([]);
  const [popupError, setPopupError] = useState(false);
  const [apiErrorModal, setApiErrorModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const [comment_call_review, setComment_call_review] = useState("");

  const formatKey = (label = "") =>
    label
      .toString()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^\w_]/g, "")
      .replace(/^./, (char) => char.toLowerCase());

  const checkFields = (role = "collaborator") => {
    const allSections = [...sectionsFirst, ...sectionsSecond];
    const emptyFields = [];
    const dataKey =
      role === "collaborator" ? "data_collaborator" : "data_partner";

    if (!eValuo?.[dataKey]) {
      allSections.forEach((section) => {
        (section.rows || []).forEach((row) => {
          emptyFields.push(`${formatKey(row)}_${role}`);
        });
      });

      if (role === "collaborator") {
        const extraFields = [
          "punti_di_forza",
          "punti_di_debolezza",
          "obiettivi_aree_miglioramento",
        ];
        emptyFields.push(...extraFields);
      }

      // partner phase 2
      if (role === "partner" && eValuo?.phase === "2") {
        emptyFields.push("obiettivi_condivisi");
      }

      setErrors(emptyFields);
      return emptyFields;
    }

    allSections.forEach((section) => {
      (section.rows || []).forEach((row) => {
        const key = `${formatKey(row)}_${role}`;
        const value = eValuo[dataKey][key];
        if (!value || value.toString().trim() === "") {
          emptyFields.push(key);
        }
      });
    });

    if (role === "collaborator") {
      const extraFields = [
        "punti_di_forza",
        "punti_di_debolezza",
        "obiettivi_aree_miglioramento",
      ];

      extraFields.forEach((field) => {
        const value = eValuo[dataKey][field];
        if (!value || value.trim() === "") {
          emptyFields.push(field);
        }
      });
    }

    if (role === "partner" && eValuo?.phase === "2") {
      const value = eValuo[dataKey]?.obiettivi_condivisi;
      if (!value || value.trim() === "") {
        emptyFields.push("obiettivi_condivisi");
      }
    }

    setErrors(emptyFields);
    return emptyFields;
  };

  const handleSubmit = async () => {
    const role = user.role;
    const dataToSave =
      role === "collaborator"
        ? eValuo.data_collaborator || {}
        : eValuo.data_partner || {};

    await service.saveEvaluation(token, id, dataToSave);

    let errorsArray = [];

    if (isProfessionista) {
      errorsArray = checkFields("collaborator");
    } else if (isPartner) {
      errorsArray = checkFields("partner");
    }

    if (errorsArray.length === 0) {
      setModalOpen(true);
    } else {
      setPopupError(true);
      setTimeout(() => setPopupError(false), 3000);
    }
  };

  const submitEvaluo = async () => {
    setApiErrorModal(false);
    setOpenSuccessModal(false);

    try {
      const role = user.role;

      const dataToSave =
        role === "collaborator"
          ? eValuo.data_collaborator || {}
          : eValuo.data_partner || {};

      await service.saveEvaluation(token, id, dataToSave);

      await service.submitEvaluation(token, id);

      setModalOpen(false);

      const updatedEvaluation = await service.getSingleEvaluation(token, id);
      setEValuo(updatedEvaluation);

      loadAppData();

      setOpenSuccessModal(true);
    } catch (error) {
      console.error("Errore", error);

      setApiErrorModal(true);

      setModalOpen(false);
    }
  };

  const submitCompleteEvaluo = async () => {
    try {
      await service.submitCallReviewEvaluation(
        token,
        id,
        eValuo?.data_partner?.obiettivi_condivisi
      );

      const updatedEvaluation = await service.getSingleEvaluation(token, id);
      setEValuo(updatedEvaluation);

      loadAppData();
      setModalOpenComplete(false);
      setOpenSuccessModal(true);
    } catch (error) {
      console.error("Errore", error);
      setApiErrorModal(true);
      setModalOpenComplete(false);
    }
  };

  useEffect(() => {
    const allSections = [...sectionsFirst, ...sectionsSecond];
    const collaboratorFields = [];
    const partnerFields = [];

    allSections.forEach((section) => {
      (section.rows || []).forEach((row) => {
        collaboratorFields.push(`${formatKey(row)}_collaborator`);
        partnerFields.push(`${formatKey(row)}_partner`);
      });
    });
  }, []);

  const [popupSuccess, setPopupSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchEvaluation();
  }, [id]);

  const fetchEvaluation = async () => {
    setLocalLoading(true);
    setApiErrorModal(false);

    try {
      const data = await service.getSingleEvaluation(token, id);
      setEValuo(data);
    } catch (error) {
      console.error("Errore", error);
      setApiErrorModal(true);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleSaveEvaluation = async () => {
    const role = user.role;

    const dataToSave =
      role === "collaborator"
        ? eValuo.data_collaborator || {}
        : eValuo.data_partner || {};

    try {
      await service.saveEvaluation(token, id, dataToSave);
      setCopySuccess("Progressi salvati con successo!");
      setPopupSuccess(true);
      setTimeout(() => setPopupSuccess(false), 3000);
      loadAppData();
      setErrors([]);
    } catch (error) {
      console.error("Errore", error);
    } finally {
    }
  };

  const handleModalClick = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);
  const handleCloseModalComplete = () => setModalOpenComplete(false);

  const handleEvaluoComplete = () => {
    const errorsArray = checkFields("partner");

    if (errorsArray.length === 0) {
      setModalOpenComplete(true);
    } else {
      setPopupError(true);
      setTimeout(() => setPopupError(false), 3000);
    }
  };

  const closeModalSuccessStart = () => {
    setOpenSuccessModal(false);
  };

  const closeModalError = () => {
    setApiErrorModal(false);
    window.location.href = "/";
  };

  return (
    <>
      {localLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress sx={{ color: "#1b2838" }} />
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        <div
          style={{
            position: "fixed",
            top: 0,
            width: "100%",
            zIndex: 1000,
          }}
        >
          <Header />
        </div>
        <Grid
          container
          style={{ display: "flex", justifyContent: "center", flex: 1 }}
        >
          <Grid
            item
            container
            xs={11}
            sm={11}
            spacing={4}
            style={{
              marginTop: "120px",
              marginBottom: "30px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <EValouForm
              user={user}
              eValuo={eValuo}
              setEValuo={setEValuo}
              errors={errors}
              id={id}
              loadAppData={loadAppData}
              setErrors={setErrors}
            />
          </Grid>
        </Grid>
        {/* box button */}
        {((eValuo.phase === "0" && isProfessionista) ||
          (eValuo.phase === "1" && isPartner)) && (
          <Grid
            container
            className="bg-blue-dark"
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              padding: "15px 20px",
              justifyContent: "flex-end",
              alignItems: "center",
              zIndex: 1200,
            }}
          >
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                "@media (max-width:399.98px)": {
                  gap: 1,
                },
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  "@media (max-width:399.98px)": { flex: 1 },
                }}
                className={
                  (user?.role === "collaborator" &&
                    eValuo?.data_collaborator === null) ||
                  (user?.role === "partner" && eValuo?.data_partner === null)
                    ? "button-bg-light-disable"
                    : "button-bg-light"
                }
                disabled={
                  (user?.role === "collaborator" &&
                    eValuo?.data_collaborator === null) ||
                  (user?.role === "partner" && eValuo?.data_partner === null)
                }
                onClick={handleSaveEvaluation}
              >
                <Typography
                  className="color-light"
                  sx={{
                    textTransform: "initial",
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: 500,
                  }}
                >
                  Salva progressi
                </Typography>
              </Button>

              <Button
                variant="contained"
                sx={{
                  "@media (max-width:399.98px)": { flex: 1 },
                }}
                className={
                  (user?.role === "collaborator" &&
                    eValuo?.data_collaborator === null) ||
                  (user?.role === "partner" && eValuo?.data_partner === null)
                    ? "button-bg-purple-disable"
                    : "button-bg-purple"
                }
                disabled={
                  (user?.role === "collaborator" &&
                    eValuo?.data_collaborator === null) ||
                  (user?.role === "partner" && eValuo?.data_partner === null)
                }
                onClick={handleSubmit}
              >
                <Typography
                  className="color-light"
                  sx={{
                    textTransform: "initial",
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: 500,
                  }}
                >
                  Conferma e invia
                </Typography>
              </Button>
            </Grid>
          </Grid>
        )}

        {eValuo.phase === "2" && isPartner && (
          <Grid
            container
            className="bg-blue-dark"
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              padding: "15px 20px",
              justifyContent: "flex-end",
              alignItems: "center",
              zIndex: 1200,
            }}
          >
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                "@media (max-width:399.98px)": {
                  flexDirection: "column",
                  gap: 1,
                },
              }}
            >
              {eValuo?.phase === "2" ? (
                <Button
                  variant="contained"
                  className="button-bg-purple"
                  sx={{ "@media (max-width:399.98px)": { width: "100%" } }}
                  onClick={handleEvaluoComplete}
                >
                  <Typography
                    className="color-light"
                    sx={{
                      textTransform: "initial",
                      fontSize: { xs: "16px", sm: "18px" },
                      fontWeight: 500,
                    }}
                  >
                    Completa
                  </Typography>
                </Button>
              ) : null}
            </Grid>
          </Grid>
        )}
      </div>

      <Modal open={modalOpen} onClose={handleCloseModal}>
        <div>
          <ModalConferma
            title="Conferma e invia"
            descriptionOne={
              user?.role === "collaborator"
                ? "Attenzione! L’invio è un’azione irreversibile con cui si inoltra automaticamente la propria autovalutazione al partner."
                : "Attenzione! L’invio è un’azione irreversibile con cui si inoltra automaticamente la propria valutazione al professionista."
            }
            descriptionTwo="Sei sicuro di voler procedere?"
            copyButton="Sì, invia"
            confirm={submitEvaluo}
            onClose={handleCloseModal}
          />
        </div>
      </Modal>

      <Modal open={modalOpenComplete} onClose={handleCloseModalComplete}>
        <div>
          <ModalConferma
            title="Conferma e completa processo di valutazione"
            descriptionOne="Attenzione! L'invio è un'azione irreversibile con cui si inoltrano automaticamente i dati della valutazione."
            descriptionTwo="Sei sicuro di voler procedere?"
            copyButton="Sì, invia"
            confirm={submitCompleteEvaluo}
            onClose={handleCloseModalComplete}
          />
        </div>
      </Modal>

      <Modal open={modalOpenComplete} onClose={handleCloseModalComplete}>
        <div>
          <ModalConferma
            title="Completa"
            descriptionOne="Attenzione! Il completamento è un’azione irreversibile con cui si la scheda di valutazione viene cristallizzata e il processo viene concluso."
            descriptionTwo="Sei sicuro di voler procedere?"
            copyButton="Sì, completa"
            confirm={submitCompleteEvaluo}
            onClose={handleCloseModalComplete}
          />
        </div>
      </Modal>

      {popupSuccess && (
        <PopupSuccess
          copySuccess={copySuccess}
          closePopupSuccess={() => setPopupSuccess(false)}
        />
      )}

      {popupError && (
        <PopupError
          copyError="Uno o più campi obbligatori sono mancanti"
          closePopupError={() => setPopupError(false)}
        />
      )}

      <Modal open={apiErrorModal} onClose={closeModalError}>
        <div>
          <ModalError
            title="Errore"
            descriptionOne="La tua richiesta non può essere inoltrata a causa di un momentaneo errore tecnico."
            descriptionTwo="Ti invitiamo a riprovare tra qualche minuto."
            copyButton="Chiudi"
            confirm={closeModalError}
            onClose={closeModalError}
          />
        </div>
      </Modal>

      <Modal open={openSuccessModal} onClose={closeModalSuccessStart}>
        <div>
          <ModalSuccess
            title={
              user?.role === "collaborator"
                ? "Autovalutazione inviata"
                : user?.role === "partner" && eValuo?.phase === "3"
                ? "Processo di valutazione concluso"
                : "Valutazione inviata"
            }
            descriptionOne={
              user?.role === "collaborator" ? (
                "Attendi che il partner completi la sua valutazione."
              ) : user?.role === "partner" && eValuo?.phase === "3" ? (
                "La scheda di valutazione sarà sempre consultabile in futuro da te, dal professionista e dal team HR di LEXIA."
              ) : (
                <>
                  <a
                    href="https://calendar.google.com/calendar/u/0/r/eventedit?text=1+to+1+%E2%80%93+Revisione+valutazione&details=Meeting+1+to+1+per+discutere+dell%27esito+della+valutazione+professionale+e+concordare+gli+obiettivi+per+il+prossimo+anno+in+LEXIA."
                    target="_blank"
                    className="color-purple"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Fissa a stretto giro una riunione
                  </a>{" "}
                  con il professionista per discutere il risultato e concordare
                  gli obiettivi per il prossimo anno (da inserire nell’apposito
                  campo in basso della valutazione).
                </>
              )
            }
            descriptionTwo={
              user?.role === "partner" && eValuo?.phase === "3"
                ? ""
                : "Solo dopo la riunione potrai concludere il processo di valutazione."
            }
            copyButton="Chiudi"
            confirm={closeModalSuccessStart}
            onClose={closeModalSuccessStart}
          />
        </div>
      </Modal>
    </>
  );
};

export default EvaluoFormPage;
